<template>
  <div
    class="Fuell-Table--row"
    :style="tableGridTemplateStyle"
  >
    <div class="Fuell-Table--item">
      <div class="flex">
        <user-name-badge
          :userName="fullUserName"
          :userAvatarId="userProfilePictureId"
        >
        </user-name-badge>
        <span class="ellipsis dark">
          {{ fullUserName}}
        </span>

      </div>
    </div>
    <div
      class="Fuell-Table--item"
      v-if="isColumnVisible('subvention_status')"
    >
      <div class="d-flex justify-content-center w-100">
        <input-checkbox
          :id="`user-social-benefit-activated-${user.id}`"
          @change="handleConfirmSubventionDeactivation"
          updateOnChange
          :value.sync="subventionStatus"
        ></input-checkbox>
      </div>

    </div>
    <div
      class="Fuell-Table--item"
      v-if="isColumnVisible('restaurants_current_assigned')"
    >
      <money-input
        :id="`meals-subvention-assigned-${user.id}`"
        :value.sync="assignedFoodSubvention"
        :allowNegative="false"
        :disabled="!hasSubvention"
        @change="handleSubventionLimits('RESTAURANTS', assignedFoodSubvention, true)"
      ></money-input>
    </div>
    <div
      class="Fuell-Table--item"
      v-if="isColumnVisible('restaurants_next_month_assigned')"
    >
      <money-input
        :id="`meals-subvention-assigned-monthly-${user.id}`"
        :value.sync="monthlyAssignedFoodSubvention"
        :allowNegative="false"
        :disabled="!hasSubvention"
        @change="handleSubventionLimits('RESTAURANTS', monthlyAssignedFoodSubvention, false)"
      ></money-input>

    </div>
    <div
      class="Fuell-Table--item"
      v-if="isColumnVisible('restaurants_spent_vs_available')"
    >
      <budget-progress-bar
        :spent='foodSpent'
        :assigned='foodAssigned'
        :budgetCardProgressTemplate='false'
        :goal='foodAssigned'
        :available='foodAvailable'
        :disabled="!hasSubvention"
      ></budget-progress-bar>
    </div>

    <div
      class="Fuell-Table--item"
      id="transporte-actual"
      v-if="isColumnVisible('transport_current_assigned')"
    >

      <money-input
        :id="`transports-subvention-assigned-${user.id}`"
        :value.sync="assignedTransportSubvention"
        :allowNegative="false"
        :disabled="!hasSubvention"
        @change="handleSubventionLimits('TRANSPORT', assignedTransportSubvention, true)"
      ></money-input>
    </div>
    <div
      class="Fuell-Table--item"
      v-if="isColumnVisible('transport_next_month_assigned')"
    >
      <money-input
        :id="`transports-subvention-assigned-monthly-${user.id}`"
        :value.sync="monthlyAssignedTransportSubvention"
        :allowNegative="false"
        :disabled="!hasSubvention"
        @change="handleSubventionLimits('TRANSPORT', monthlyAssignedTransportSubvention, false)"
      ></money-input>
    </div>
    <div
      class='Fuell-Table--item'
      v-if="isColumnVisible('transport_spent_vs_available')"
    >
      <budget-progress-bar
        :spent='transportSpent'
        :assigned='transportAssigned'
        :budgetCardProgressTemplate='false'
        :goal='transportAssigned'
        :available='transportAvailable'
        alternateColor
        :disabled="!hasSubvention"
      ></budget-progress-bar>

    </div>

    <div
      class="Fuell-Table--item"
      v-if="isColumnVisible('nursery_current_assigned')"
    >
      <template v-if='!isImpersonating'>
        <span class="d-flex w-100 justify-content-center">

          {{ nurseryAssignedSubvention | currency('€', 2, {
            symbolOnLeft: false,
            thousandsSeparator: '.',
            decimalSeparator: ','
          }) }}</span>
      </template>
      <template v-else>
        <money-input
          :id="'nurseryLimitInput'"
          :value.sync='assignedNurserySubvention'
          :disabled="!hasSubvention"
          @change="handleSubventionLimits('NURSERY',assignedNurserySubvention, true)"
          :allowNegative='false'
        ></money-input>

      </template>
    </div>
    <div
      class='Fuell-Table--item d-flex justify-content-around align-items-center'
      v-if="isColumnVisible('insurance_current_assigned')"
    >

      <template v-if='!isImpersonating'>
        <span class="w-50">
          {{ insuranceAssignedSubvention | currency('€', 2, {
            symbolOnLeft: false,
            thousandsSeparator: '.',
            decimalSeparator: ','
          }) }}</span>
      </template>
      <template v-else>
        <div
          class="w-100"
          @click="showPolicyHoldersWarning"
        >
          <money-input
            :id="'insuranceLimitInput'"
            :value.sync='assignedInsuranceSubvention'
            :disabled="disableInsuranceAssignationInput"
            @click="showPolicyHoldersWarning"
            @change="handleSubventionLimits('INSURANCE',assignedInsuranceSubvention, true)"
            :allowNegative='false'
          ></money-input>
        </div>
      </template>

    </div>
    <div
      class='Fuell-Table--item d-flex justify-content-around align-items-center'
      v-if="isColumnVisible('insurance_policy_holder')"
    >

      <template v-if='!isImpersonating'>
        <span class="d-flex w-100 justify-content-center">
          {{ insurancePolicyHolders}}
        </span>
      </template>
      <template v-else>
        <money-input
          :id="'insurancePolicyHolderInput'"
          :value.sync='insurancePolicyHoldersValue'
          hideCurrency
          :disabled='!hasSubvention '
          @change="setInsuranceHolders('regular')"
          :allowNegative='false'
        ></money-input>

      </template>

    </div>

    <div
      class='Fuell-Table--item d-flex justify-content-around align-items-center'
      v-if="isColumnVisible('insurance_disabled_policy_holders')"
    >
      <template v-if='!isImpersonating'>
        <span class="d-flex w-100 justify-content-center">
          {{insuranceDisabledPolicyHolders}}
        </span>
      </template>
      <template v-else>
        <money-input
          :id="'insuranceDisabledPolicyHolders'"
          :value.sync='insuranceDisabledPolicyHoldersValue'
          hideCurrency
          :disabled='!hasSubvention '
          @change="setInsuranceHolders('disabled')"
          :allowNegative='false'
        ></money-input>
      </template>
    </div>
    <div
      class='Fuell-Table--item'
      v-if="isColumnVisible('education_current_assigned')"
    >

      <template v-if='!isImpersonating'>
        <span class="d-flex w-100 justify-content-center">

          {{ educationAssignedSubvention | currency('€', 2, {
            symbolOnLeft: false,
            thousandsSeparator: '.',
            decimalSeparator: ','
          }) }}</span>
      </template>
      <template v-else>
        <money-input
          :id="'educationLimitInput'"
          :value.sync='assignedEducationSubvention'
          :disabled='!hasSubvention '
          @change="handleSubventionLimits('EDUCATION',assignedEducationSubvention, true)"
          :allowNegative='false'
        ></money-input>

      </template>

    </div>
  </div>
</template>

<script>
import MoneyInput from '@/components/Commons/Forms/MoneyInput'
import InputCheckbox from '@/components/Commons/Forms/InputCheckbox'
import UserNameBadge from '@/components/Commons/UserNameBadge'
import _userSubventionHandler from '../../../_mixins/_userSubventionHandler'
import _userBenefitsRowsCommons from '../../../_mixins/_userBenefitsRowsCommons'
import _benefitsSharedMethods from '../../../_mixins/_benefitsSharedMethods'
import BudgetProgressBar from '@/modules/budgets/_components/_shared/BudgetProgressBar'

export default {
  name: 'UsersSocialBenefitsTableRow',
  props: {
    user: {
      type: Object
    },
    columns: {
      type: Array
    }
  },
  mixins: [_userSubventionHandler, _userBenefitsRowsCommons, _benefitsSharedMethods],
  components: {
    UserNameBadge,
    InputCheckbox,
    MoneyInput,
    BudgetProgressBar
  },
  data () {
    return {
      subventionStatus: false,
      assignedFoodSubvention: 0,
      monthlyAssignedFoodSubvention: 0,
      assignedTransportSubvention: 0,
      monthlyAssignedTransportSubvention: 0,
      assignedNurserySubvention: 0,
      assignedInsuranceSubvention: 0,
      assignedEducationSubvention: 0,
      insurancePolicyHoldersValue: 0,
      insuranceDisabledPolicyHoldersValue: 0,
      budgetToUpdate: {}
    }
  },
  computed: {
    tableGridTemplateStyle () {
      return {
        gridTemplateColumns: this.columns.filter(c => this.isColumnVisible(c.key)).map(c => c.template).join(' '),
        gridGap: '15px'
      }
    }
  },
  methods: {
    _initialize () {
      this.subventionStatus = this.hasSubvention
      this.assignedFoodSubvention = this.foodAssignedSubvention
      this.monthlyAssignedFoodSubvention = this.foodMonthlyAssignedSubvention
      this.assignedTransportSubvention = this.transportAssignedSubvention
      this.monthlyAssignedTransportSubvention = this.transportMonthlyAsignedSubvention
      this.assignedNurserySubvention = this.nurseryAssignedSubvention
      this.assignedEducationSubvention = this.educationAssignedSubvention
      this.assignedInsuranceSubvention = this.insuranceAssignedSubvention
      this.insurancePolicyHoldersValue = this.insurancePolicyHolders
      this.insuranceDisabledPolicyHoldersValue = this.insuranceDisabledPolicyHolders
    }

  },

  created () {
    this._initialize()
  },
  watch: {
    hasSubvention: function (newV, oldV) {
      this.subventionStatus = newV
    },
    foodAssignedSubvention: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.assignedFoodSubvention = newV
      }
    },
    foodMonthlyAssignedSubvention: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.monthlyAssignedFoodSubvention = newV
      }
    },
    transportAssignedSubvention: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.assignedTransportSubvention = newV
      }
    },
    transportMonthlyAsignedSubvention: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.monthlyAssignedTransportSubvention = newV
      }
    },
    nurseryAssignedSubvention: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.assignedNurserySubvention = newV
      }
    },
    nurseryMonthlyAsignedSubvention: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.monthlyAssignedNurserySubvention = newV
      }
    },
    educationAssignedSubvention: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.assignedEducationSubvention = newV
      }
    },
    insuranceAssignedSubvention: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.assignedInsuranceSubvention = newV
      }
    },
    insurancePolicyHolders: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.insurancePolicyHoldersValue = newV
      }
    },
    insuranceDisabledPolicyHolders: function (newV, oldV) {
      if (newV !== oldV && newV >= 0) {
        this.insuranceDisabledPolicyHoldersValue = newV
      }
    }

  }

}

</script>

<style scoped>
.Fuell-Table--row,
.Fuell-Table--row:hover {
  width: fit-content;
}

.Fuell-Table--item {
  padding: 0px;
}
.Fuell-table--item >>> .assigned--red {
  background-color: var(--semantic-negative-main);
}
.Fuell-Table--item >>> .progress--assigned {
  background-color: var(--semantic-brand-main);
}
</style>
